import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { User, Calendar, CheckSquare, Clock, CheckCircle2, Archive } from 'lucide-react';
import { checklistAssignmentsApi, checklistInstancesApi, ChecklistItemInstance, projectsApi, usersApi } from '../lib/api';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { LoadingSpinner } from './LoadingScreen';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '../hooks/useTelegram';
import { useAuthStore } from '../store/auth';

export function SubordinatesChecklists() {
  const DEV = import.meta.env.DEV;
  DEV && console.log('[SubordinatesChecklists] Component mounting...');
  
  const navigate = useNavigate();
  const { hapticFeedback, showAlert } = useTelegram();
  const currentUser = useAuthStore((state: any) => state.user);
  const queryClient = useQueryClient();
  
  // Принудительно обновляем данные пользователя при монтировании компонента
  useEffect(() => {
    DEV && console.log('[SubordinatesChecklists] useEffect: invalidating queries');
    queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
  }, [queryClient]);
  
  useEffect(() => {
    DEV && console.log('[SubordinatesChecklists] Component mounted');
    return () => {
      DEV && console.log('[SubordinatesChecklists] Component unmounting');
    };
  }, []);

  // Получаем данные пользователя
  // Принудительно обновляем данные при загрузке страницы
  const { data: userData } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      try {
        return await usersApi.me();
      } catch (error: any) {
        // Игнорируем ошибки 404/403 - пользователь будет перенаправлен на страницу авторизации
        if (error?.status === 404 || error?.status === 403) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 0, // Всегда считаем данные устаревшими
    gcTime: 0, // Не кешируем
    refetchOnMount: true, // Всегда обновляем при монтировании
    refetchOnWindowFocus: true, // Обновляем при фокусе окна
    retry: false, // Не повторяем запрос при ошибке
  });

  const user = userData || currentUser;
  const currentUserId = user?.id;

  // Получаем проекты для проверки роли
  const { data: projects, error: projectsError } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      try {
        return await projectsApi.list();
      } catch (error: any) {
        // Игнорируем ошибки 404/403 - возвращаем пустой массив
        if (error?.status === 404 || error?.status === 403) {
          return [];
        }
        throw error;
      }
    },
    retry: false, // Не повторяем запрос при ошибке
  });

  // Системный админ или менеджер в проектах
  // Проверяем и userData, и currentUser
  // Также проверяем роль SYSTEM_ADMIN в проектах для системных админов
  const isSystemAdminFromData = Boolean((userData as any)?.isSystemAdmin);
  const isSystemAdminFromStore = Boolean((currentUser as any)?.isSystemAdmin);
  const hasSystemAdminRole = projects?.some((p: any) => p.role === 'SYSTEM_ADMIN') || false;
  const isSystemAdmin = isSystemAdminFromData || isSystemAdminFromStore || hasSystemAdminRole;
  const hasManagerRole = projects?.some((p: any) => ['ADMIN', 'MANAGER'].includes(p.role)) || false;
  const isManager = isSystemAdmin || hasManagerRole;
  
  // Получаем все назначения (API автоматически фильтрует по доступу)
  const { data: assignments, isLoading, error: assignmentsError } = useQuery({
    queryKey: ['checklist-assignments-subordinates'],
    queryFn: async () => {
      try {
        return await checklistAssignmentsApi.list({ status: 'ACTIVE' });
      } catch (error: any) {
        // Игнорируем ошибки 404/403 - возвращаем пустой массив
        if (error?.status === 404 || error?.status === 403) {
          return [];
        }
        throw error;
      }
    },
    enabled: Boolean(isManager), // Убеждаемся, что isManager определен
    retry: false, // Не повторяем запрос при ошибке
  });

  // Получаем экземпляры для всех назначений
  const { data: allInstances, error: instancesError } = useQuery({
    queryKey: ['checklist-instances-subordinates'],
    queryFn: async () => {
      try {
        return await checklistInstancesApi.list({});
      } catch (error: any) {
        // Игнорируем ошибки 404/403 - возвращаем пустой массив
        if (error?.status === 404 || error?.status === 403) {
          return [];
        }
        throw error;
      }
    },
    enabled: Boolean(isManager) && !!assignments && assignments.length > 0,
    retry: false, // Не повторяем запрос при ошибке
  });

  // Мутация для архивации/разархивации мероприятия - ВСЕГДА вызываем, до любых return
  const archiveMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'ARCHIVED' }) =>
      checklistAssignmentsApi.update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-assignments-subordinates'] });
      hapticFeedback('success');
      showAlert('Мероприятие обновлено');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось изменить статус');
    },
  });

  // Показываем загрузку, пока определяем статус менеджера или загружаются проекты
  DEV && console.log('[SubordinatesChecklists] Render check:', { 
    projects: projects !== undefined ? 'loaded' : 'loading',
    isManager,
    projectsLength: projects?.length,
  });
  
  if (projects === undefined || isManager === undefined) {
    DEV && console.log('[SubordinatesChecklists] Showing loading - waiting for projects or isManager');
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isManager) {
    DEV && console.log('[SubordinatesChecklists] User is not a manager, showing access denied');
    return (
      <div className="p-4 bg-white">
        <Card className="p-8 text-center">
          <User className="w-12 h-12 mx-auto mb-4 text-tg-hint" />
          <p className="text-tg-hint">Доступно только для руководителей</p>
        </Card>
      </div>
    );
  }
  
  DEV && console.log('[SubordinatesChecklists] User is manager, proceeding to render content');

  // Показываем ошибки, если они есть
  if (projectsError && projectsError?.status !== 404 && projectsError?.status !== 403) {
    console.error('[SubordinatesChecklists] Projects error:', projectsError);
    return (
      <div className="p-4 bg-white">
        <Card className="p-8 text-center">
          <p className="text-red-500 mb-2">Ошибка загрузки проектов</p>
          <p className="text-sm text-tg-hint">{(projectsError as any)?.message || 'Не удалось загрузить проекты'}</p>
        </Card>
      </div>
    );
  }

  if (assignmentsError && assignmentsError?.status !== 404 && assignmentsError?.status !== 403) {
    console.error('[SubordinatesChecklists] Assignments error:', assignmentsError);
    return (
      <div className="p-4 bg-white">
        <Card className="p-8 text-center">
          <p className="text-red-500 mb-2">Ошибка загрузки назначений</p>
          <p className="text-sm text-tg-hint">{(assignmentsError as any)?.message || 'Не удалось загрузить назначения'}</p>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    DEV && console.log('[SubordinatesChecklists] Loading assignments...');
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  DEV && console.log('[SubordinatesChecklists] Rendering main content');

  // Фильтруем назначения: только те, где текущий пользователь НЕ является assignee
  // (т.е. назначенные подчиненным)
  // API уже возвращает только те назначения, к которым у нас есть доступ (мы руководитель проекта)
  const subordinatesAssignments = (assignments || []).filter((a: any) => {
    // Проверяем, что это назначение подчиненному (не самому себе)
    return a.assigneeId !== currentUserId;
  });

  DEV && console.log('[SubordinatesChecklists] Rendering with:', {
    assignmentsCount: assignments?.length || 0,
    subordinatesAssignmentsCount: subordinatesAssignments.length,
    currentUserId,
    isLoading,
  });

  const getInstancesForAssignment = (assignmentId: string): ChecklistItemInstance[] => {
    if (!allInstances) return [];
    return allInstances.filter((inst: ChecklistItemInstance) => inst.assignmentId === assignmentId);
  };

  const getStatusStats = (instances: ChecklistItemInstance[]) => {
    const stats = {
      total: instances.length,
      completed: instances.filter(i => i.status === 'COMPLETED' || i.status === 'ACCEPTED').length,
      pending: instances.filter(i => i.status === 'COMPLETED_PENDING').length,
      rejected: instances.filter(i => i.status === 'REJECTED').length,
      inProgress: instances.filter(i => i.status === 'IN_PROGRESS').length,
    };
    return stats;
  };

  DEV && console.log('[SubordinatesChecklists] About to render main content, subordinatesAssignments:', subordinatesAssignments.length);
  
  return (
    <div className="p-4 space-y-4 bg-white min-h-screen">
      {/* Кнопка для назначения */}
      <Card className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 border-0">
        <button
          onClick={() => {
            navigate('/checklists/new');
            hapticFeedback('medium');
          }}
          className="w-full flex items-center justify-center gap-3 bg-white text-blue-600 rounded-xl px-6 py-4 font-semibold text-base active:scale-[0.98] transition-transform shadow-lg hover:shadow-xl"
        >
          <CheckSquare className="w-6 h-6" />
          <span>Создать Чек-лист подчинённому</span>
        </button>
      </Card>

      {/* Список назначений подчиненных */}
      <div className="space-y-3">
        {subordinatesAssignments.length === 0 ? (
          <Card className="p-8 text-center">
            <User className="w-12 h-12 mx-auto mb-4 text-tg-hint" />
            <p className="text-tg-hint mb-2">Нет назначенных чек-листов подчинённым</p>
            <p className="text-xs text-tg-hint">Назначьте чек-лист, чтобы начать отслеживать работу подчинённых</p>
          </Card>
        ) : (
          subordinatesAssignments.map((assignment: any) => {
            // Защита от undefined/null
            if (!assignment || !assignment.id) {
              DEV && console.warn('[SubordinatesChecklists] Invalid assignment:', assignment);
              return null;
            }

            try {
              const instances = getInstancesForAssignment(assignment.id);
              const stats = getStatusStats(instances);

              // Безопасная обработка дат
              let activeFromStr = 'не указано';
              let activeToStr = 'бессрочно';
              
              try {
                if (assignment.activeFrom) {
                  activeFromStr = format(new Date(assignment.activeFrom), 'dd.MM.yyyy', { locale: ru });
                }
                if (assignment.activeTo) {
                  activeToStr = format(new Date(assignment.activeTo), 'dd.MM.yyyy', { locale: ru });
                }
              } catch (dateError) {
                DEV && console.warn('[SubordinatesChecklists] Error formatting date:', dateError);
              }

              return (
                <Card 
                  key={assignment.id} 
                  variant="interactive"
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    hapticFeedback('light');
                    if (assignment.templateId) {
                      navigate(`/checklists/${assignment.templateId}/edit`);
                    } else {
                      showAlert('Ошибка: отсутствует ID шаблона');
                    }
                  }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-tg-text mb-1">
                        {assignment.template?.title || 'Без названия'}
                      </h3>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="default">
                          {assignment.template?.type === 'REGULATION' && 'Регламентный'}
                          {assignment.template?.type === 'EVENT' && 'Мероприятие'}
                          {assignment.template?.type === 'PERSONAL' && 'Личный'}
                          {!assignment.template?.type && 'Неизвестный'}
                        </Badge>
                        {assignment.status === 'ACTIVE' && (
                          <Badge variant="success">Активен</Badge>
                        )}
                        {assignment.status === 'ARCHIVED' && (
                          <Badge variant="default">Архив</Badge>
                        )}
                      </div>
                    </div>
                    {/* Кнопка архивации для мероприятий */}
                    {assignment.template?.type === 'EVENT' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          hapticFeedback('light');
                          archiveMutation.mutate({
                            id: assignment.id,
                            status: assignment.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE',
                          });
                        }}
                        className="p-2 text-gray-500 hover:text-blue-500 transition-colors"
                        title={assignment.status === 'ACTIVE' ? 'Архивировать' : 'Восстановить'}
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Информация о подчиненном */}
                  <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-tg-text font-medium">
                      {assignment.assignee?.firstName || ''} {assignment.assignee?.lastName || ''}
                      {!assignment.assignee && 'Не назначен'}
                    </span>
                  </div>

                  {/* Статистика по делам */}
                  {stats.total > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                          <span className="text-xs text-gray-600">Выполнено</span>
                        </div>
                        <p className="text-lg font-bold text-green-600 mt-1">{stats.completed}</p>
                      </div>
                      <div className="p-2 bg-yellow-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-yellow-600" />
                          <span className="text-xs text-gray-600">Ожидает</span>
                        </div>
                        <p className="text-lg font-bold text-yellow-600 mt-1">{stats.pending}</p>
                      </div>
                    </div>
                  )}

                  {/* Период активности */}
                  <div className="flex items-center gap-2 text-sm text-tg-hint">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {activeFromStr} - {activeToStr}
                    </span>
                  </div>

                  {/* Всего задач */}
                         {stats.total > 0 && (
                           <div className="mt-2 pt-2 border-t border-gray-200">
                             <p className="text-xs text-tg-hint">
                               Всего дел: <span className="font-semibold text-tg-text">{stats.total}</span>
                             </p>
                           </div>
                         )}
                </Card>
              );
            } catch (error: any) {
              console.error('[SubordinatesChecklists] Error rendering assignment:', assignment.id, error);
              return (
                <Card key={assignment.id} className="p-4 border-red-200 bg-red-50">
                  <p className="text-red-600 text-sm">Ошибка отображения назначения</p>
                  <p className="text-xs text-red-400 mt-1">{error?.message || 'Неизвестная ошибка'}</p>
                </Card>
              );
            }
          }).filter(Boolean) // Убираем null значения
        )}
      </div>
    </div>
  );
}

